import moment from 'moment'
import { orderBy } from 'lodash-es'
import { formatUrl } from '@/helpers/content-formatting'

export default Vue => {
  const baseUrl = process.env.VUE_APP_API_URL.replace(/\/+$/, '')

  const generatePagedRestRoutes = (route, totalCount, perPage = 100) => {
    const totalPages = Math.ceil(totalCount / perPage)

    return Array.from({ length: totalPages }, (_, i) => {
      const page = i + 1
      return `${route}&page=${page}&per_page=${perPage}`
    })
  }

  const fetchAll = (route, totalCount) => {
    return Promise.all(generatePagedRestRoutes(route, totalCount).map(pageRoute => fetch(pageRoute)))
      .then(responses => Promise.all(responses.map(res => res.json())))
      .then(pages => {
        return Promise.resolve(pages.flat())
      })
  }

  const WpApiVM = new Vue({
    name: 'WpApiVM',
    data() {
      return {
        baseUrl,
        pagesLoaded: false,
        secondaryDataLoaded: false,
        pages: {},
        projects: {},
        events: {},
        allPublications: {},
        authors: {},
        keywords: {},
        apprendreAgirPosts: null
      }
    },
    computed: {
      keywordsWithMatches() {
        const entries = [
          ...this.restPublications,
          ...this.eventPublications,
          ...this.newsPublications,
          ...this.monitoringPublications,
          ...Object.values(this.projects)
        ]

        return Object.values(this.keywords).reduce((acc, keyword) => {
          const hasMatch = entries.some(({ keywords }) => keywords && keywords.includes(keyword.id))
          return hasMatch ? [...acc, keyword] : acc
        }, [])
      },
      keywordsById() {
        return Object.values(this.keywords).reduce((acc, keyword) => {
          acc[keyword.id] = keyword
          return acc
        }, {})
      },
      authorsById() {
        return Object.values(this.authors).reduce((acc, author) => {
          acc[author.id] = author
          return acc
        }, {})
      },
      publicationsById() {
        return Object.values(this.allPublications).reduce((acc, pub) => {
          acc[pub.id] = pub
          return acc
        }, {})
      },
      eventPublications() {
        return orderBy(Object.values(this.events), ['date'], ['desc'])
      },
      monitoringPublications() {
        return orderBy(
          Object.values(this.allPublications).filter(pub => pub.type === 'monitoring'),
          ['original_publish_date'],
          ['desc']
        )
      },
      newsPublications() {
        return orderBy(
          Object.values(this.allPublications).filter(pub => pub.type === 'news'),
          ['original_publish_date'],
          ['desc']
        )
      },
      restPublications() {
        return orderBy(
          Object.values(this.allPublications).filter(pub => pub.type !== 'monitoring' && pub.type !== 'news'),
          ['original_publish_date'],
          ['desc']
        )
      }
    },
    created() {
      // Fetch external posts
      fetch(`https://icea-apprendreagir.ca/?rest_route=/wp/v2/posts`)
        .then(res => res.json())
        .then(data => {
          Promise.all(
            data.map(({ id, date, featured_media: mediaId, title, content, link }) => {
              if (!mediaId) {
                return Promise.resolve({
                  id,
                  date,
                  title: title.rendered,
                  content: content.rendered,
                  link,
                  image: null
                })
              }

              return fetch(`https://icea-apprendreagir.ca/?rest_route=/wp/v2/media/${mediaId}`)
                .then(mediaRes => mediaRes.json())
                .then(media => ({
                  id,
                  date,
                  title: title.rendered,
                  link,
                  content: content.rendered,
                  image: media.media_details.sizes.medium.source_url
                }))
            })
          ).then(posts => {
            this.apprendreAgirPosts = posts
          })
        })

      // Main content fetch, prevents site rendering until data is loaded
      Promise.all([fetch(`${baseUrl}/?rest_route=/wcd/v1/pages`), fetch(`${baseUrl}/?rest_route=/wcd/v1/data/project`)])
        .then(responses => Promise.all(responses.map(res => res.json())))
        .then(([pagesData, projectsData]) => {
          this.pages = Object.values(pagesData).reduce((acc, page) => {
            acc[page.slug] = { id: page.id, slug: page.slug, ...page.data }
            return acc
          }, {})

          this.projects = Object.values(projectsData).reduce((acc, { id, slug, data }) => {
            acc[slug] = { id, slug, ...data }
            return acc
          }, {})

          this.pagesLoaded = true
        })

      // Secondary fetch, prevents rendering of search and publications
      Promise.all([
        fetch(`${baseUrl}/?rest_route=/wp/v2/keyword&per_page=1`),
        fetch(`${baseUrl}/?rest_route=/wp/v2/custom_author&per_page=1`),
        fetch(`${baseUrl}/?rest_route=/wcd/v1/data/publication`),
        fetch(`${baseUrl}/?rest_route=/wcd/v1/data/event`)
      ]).then(([keywordsRes, authorsRes, publicationsRes, eventsRes]) => {
        const keywordCount = keywordsRes.headers.get('X-WP-Total')
        const authorCount = authorsRes.headers.get('X-WP-Total')

        const fetchAllKeywords = fetchAll(`${baseUrl}/?rest_route=/wp/v2/keyword`, keywordCount)
        const fetchAllAuthors = fetchAll(`${baseUrl}/?rest_route=/wp/v2/custom_author`, authorCount)

        Promise.all([fetchAllKeywords, fetchAllAuthors, publicationsRes.json(), eventsRes.json()]).then(
          ([keywordsData, authorsData, publicationsData, eventsData]) => {
            this.keywords = keywordsData.reduce((acc, keyword) => {
              const { id, slug, acf, count, name } = keyword
              acc[slug] = { id, slug, ...acf, count, name }
              return acc
            }, {})

            this.authors = authorsData.reduce((acc, author) => {
              const { id, slug, name } = author
              acc[slug] = { id, slug, name }
              return acc
            }, {})

            this.allPublications = Object.values(publicationsData).reduce((acc, { id, slug, date, data }) => {
              const formattedDate = Number(data.original_publish_date) * 1000 || moment(date).valueOf()

              const { images = '', attachments = '' } = data
              const imageUrls = images
                .split(',')
                .map(img => img.trim())
                .filter(Boolean)
                .map(img => formatUrl(img))

              const attachmentUrls = attachments
                .split(',')
                .map(att => att.trim())
                .filter(Boolean)
                .map(att => formatUrl(att))

              acc[slug] = {
                id,
                slug,
                ...data,
                original_publish_date: formattedDate,
                images: imageUrls,
                attachments: attachmentUrls
              }

              return acc
            }, {})

            this.events = Object.values(eventsData).reduce((acc, { id, slug, data }) => {
              const { images = '', attachments = '', date = 0, eventType } = data
              const safeDate = date || 0
              const sortableDate =
                typeof safeDate === 'string' ? moment(safeDate, 'YYYY/MM/DD').valueOf() : moment(safeDate).valueOf()

              const imageUrls = images
                .split(',')
                .map(img => img.trim())
                .filter(Boolean)
                .map(img => formatUrl(img, true))

              const attachmentUrls = attachments
                .split(',')
                .map(att => att.trim())
                .filter(Boolean)
                .map(att => formatUrl(att, true))

              acc[slug] = {
                id,
                slug,
                ...data,
                eventType,
                date: sortableDate,
                images: imageUrls,
                attachments: attachmentUrls
              }
              return acc
            }, {})

            this.secondaryDataLoaded = true
          }
        )
      })
    },
    methods: {
      getPageRouteById(pageId, fallback = 'home') {
        if (!pageId) return { name: fallback }
        const { slug } = Object.values(this.$wp.pages).find(({ id }) => id === pageId) || {}
        return slug ? { name: slug } : { name: 'resources' }
      }
    }
  })

  Vue.prototype.$wp = WpApiVM
}
